/**
 * Raportin lukijat (#1475): people named to read this Kuutio's report — its
 * restricted sections and chat — without taking part in the workshop. Every
 * participant reads the report already; this list is for the others. Adding
 * one mails the invitation (raportti_kutsu) with the report link; the reader
 * signs in on the report with a code sent to that address.
 *
 * The Kuutio's managers only, as on the server: for anyone else the list
 * answers 403 and the section is not drawn.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Mail, Trash2 } from 'lucide-react';
import { PrimaryButton, SectionLabel, StatusMessage } from './reportAdminStyles';
import { API_ENDPOINTS } from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Lead = styled.p`
  color: #888;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const Input = styled.input`
  flex: 1 1 220px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #333;
  background: rgba(255,255,255,0.05);
  color: #eee;
  font-size: 14px;
  &:focus { outline: none; border-color: #4a9eff; }
`;

const List = styled.ul`
  list-style: none;
  margin: 0 0 40px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(255,255,255,0.04);
  border: 1px solid #262626;
`;

const Who = styled.div`
  flex: 1;
  min-width: 0;
  color: #ddd;
  font-size: 14px;
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  color: #777;
  font-size: 12px;
  margin-top: 2px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid #333;
  background: transparent;
  color: #aaa;
  cursor: pointer;
  &:hover:not(:disabled) { color: #fff; background: rgba(255,255,255,0.08); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

async function request(url, options = {}) {
  const res = await fetch(url, { credentials: 'include', ...options });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status });
  return body;
}

const fmtDate = (iso, language) => (iso ? new Date(iso).toLocaleDateString(language) : null);

export default function ReportReadersSection({ instanceId }) {
  const { t, language } = useTranslation();
  const [readers, setReaders] = useState(null); // null: loading or not a manager
  const [allowed, setAllowed] = useState(true);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      setReaders((await request(API_ENDPOINTS.INSTANCE_REPORT_READERS(instanceId))).readers || []);
    } catch (err) {
      if (err.status === 401 || err.status === 403) setAllowed(false);
      else setStatus({ text: err.message, error: true });
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const mailStatus = (mail) => (mail?.sent
    ? { text: t('reportGen.readers.invited') }
    : { text: t('reportGen.readers.inviteFailed', { error: (mail?.errors || []).join(', ') }), error: true });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const r = await request(API_ENDPOINTS.INSTANCE_REPORT_READERS(instanceId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      setStatus(r.alreadyIn && !r.mail ? { text: t('reportGen.readers.alreadyParticipant') } : mailStatus(r.mail));
      setEmail('');
      await load();
    } catch (err) {
      setStatus({ text: err.message, error: true });
    }
    setBusy(false);
  };

  const invite = async (reader) => {
    setStatus(null);
    try {
      setStatus(mailStatus(await request(API_ENDPOINTS.INSTANCE_REPORT_READER_INVITE(instanceId, reader.id), { method: 'POST' })));
      await load();
    } catch (err) {
      setStatus({ text: err.message, error: true });
    }
  };

  const remove = async (reader) => {
    setStatus(null);
    try {
      await request(API_ENDPOINTS.INSTANCE_REPORT_READER(instanceId, reader.id), { method: 'DELETE' });
      await load();
    } catch (err) {
      setStatus({ text: err.message, error: true });
    }
  };

  if (!allowed) return null;

  return (
    <section>
      <SectionLabel>{t('reportGen.readers.title')}</SectionLabel>
      <Lead>{t('reportGen.readers.lead')}</Lead>
      {status && <StatusMessage $error={status.error}>{status.text}</StatusMessage>}
      <Form onSubmit={add}>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('reportGen.readers.emailPlaceholder')}
          aria-label={t('reportGen.readers.emailPlaceholder')}
          disabled={busy}
        />
        <PrimaryButton type="submit" disabled={busy || !email.includes('@')}>
          <Mail size={16} />
          {t('reportGen.readers.add')}
        </PrimaryButton>
      </Form>
      <List>
        {readers?.length === 0 && <Meta as="li">{t('reportGen.readers.empty')}</Meta>}
        {(readers || []).map((r) => (
          <Row key={r.id}>
            <Who>
              {r.name ? `${r.name} · ${r.email}` : r.email}
              <Meta>
                {r.invitedAt
                  ? t('reportGen.readers.invitedOn', { date: fmtDate(r.invitedAt, language) })
                  : t('reportGen.readers.notInvited')}
                {r.lastLoginAt && ` · ${t('reportGen.readers.lastLogin', { date: fmtDate(r.lastLoginAt, language) })}`}
              </Meta>
            </Who>
            <IconButton type="button" onClick={() => invite(r)} title={t('reportGen.readers.resend')} aria-label={t('reportGen.readers.resend')}>
              <Mail size={16} />
            </IconButton>
            <IconButton type="button" onClick={() => remove(r)} title={t('reportGen.readers.remove')} aria-label={t('reportGen.readers.remove')}>
              <Trash2 size={16} />
            </IconButton>
          </Row>
        ))}
      </List>
    </section>
  );
}
